import { useState, useRef } from "react";
import { Phone, Mail, MapPin, Check, ArrowUpRight, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SITE, MEDIA } from "@/lib/site";
import Seo from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";

const CONTACT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "url": "https://rvasigns.com/contact",
  "name": "Contact Todd Friedman — RVA Signs",
  "description": "Send a project or sign quote to Todd Friedman in Richmond, Virginia.",
  "mainEntity": {
    "@type": "Person",
    "name": "Todd Friedman",
    "telephone": "+1-804-539-7300",
    "email": "todd@rvasigns.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Richmond",
      "addressRegion": "VA",
      "addressCountry": "US"
    }
  }
};

const PROJECT_TYPES = [
  "New Sign",
  "Replace Existing Sign",
  "Sign Repair",
  "Need Advice / Not Sure",
];

const INITIAL = {
  name: "",
  company: "",
  phone: "",
  email: "",
  project_address: "",
  project_type: "",
  project_description: "",
};

const MAX_FILE_MB = 5;

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum ${MAX_FILE_MB}MB.`);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.project_type) {
      toast.error("Please fill in your name, phone, email, and project type.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("attachment", file);
      await api.post("/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackEvent("contact_form_submit", {
        project_type: form.project_type,
        has_attachment: !!file,
      });
      setSubmitted(true);
      toast.success("Thanks. Todd will be in touch shortly.");
      setForm(INITIAL);
      clearFile();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please call Todd directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        path="/contact"
        title="Contact Todd"
        description="Send Todd a sign quote, a project description, or a question. Phone, email, or upload directly from the form."
        jsonLd={CONTACT_JSONLD}
      />
      {/* HERO */}
      <section className="pt-20 lg:pt-28 pb-12 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-10 h-0.5 bg-[#F97316]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Talk to Todd</span>
            </div>
            <h1 className="font-heading text-[#1A1A1A] tracking-tight leading-[0.98]" style={{ fontWeight: 900, fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)" }} data-testid="contact-headline">
              Tell me about your project.
            </h1>
            {/* Mobile/tablet thumbnail — circular, never crops the face awkwardly */}
            <div className="lg:hidden mt-8" data-testid="contact-mobile-portrait">
              <img
                src={MEDIA.toddPortrait}
                alt="Todd Friedman"
                className="block w-40 h-40 rounded-full object-cover object-top bg-[#F8F8F5] border border-[#1A1A1A]/10"
                loading="lazy"
              />
            </div>
            <div className="mt-8 max-w-2xl space-y-4 text-lg text-[#2B2F33]/85 leading-relaxed" data-testid="contact-subheadline">
              <p>Got a project? Got a quote? Not sure where to start?</p>
              <p>Send Todd the details below and he&rsquo;ll get back to you. Todd usually responds within one business day.</p>
              <p className="text-[#1A1A1A] font-semibold">Call, text, or email.</p>
              <p>No pitch. No pressure.</p>
              <p>If I can help, I will. If I can&rsquo;t, I&rsquo;ll point you in the right direction.</p>
            </div>
          </div>
          <div className="lg:col-span-4 space-y-4 text-sm font-semibold">
            <div className="hidden lg:block w-24 h-24 rounded-full overflow-hidden bg-[#F8F8F5] ring-2 ring-[#F97316]/20" data-testid="contact-avatar">
              <img
                src={MEDIA.toddPortrait}
                alt="Todd Friedman"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <a href={SITE.phoneHref} className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#F97316] transition-colors text-xl" data-testid="contact-phone">
              <Phone className="w-5 h-5 text-[#F97316]" /> {SITE.phone}
            </a>
            <a href={SITE.emailHref} className="flex items-center gap-3 text-[#2B2F33]/85 hover:text-[#F97316] transition-colors break-all" data-testid="contact-email">
              <Mail className="w-4 h-4 text-[#F97316]" /> {SITE.email}
            </a>
            <div className="flex items-center gap-3 text-[#2B2F33]/85">
              <MapPin className="w-4 h-4 text-[#F97316]" /> {SITE.address}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="hidden lg:block img-zoom mb-8 max-w-sm mx-auto lg:mx-0" data-testid="contact-todd-portrait">
              <img
                src={MEDIA.toddPortrait}
                alt="Todd Friedman"
                className="block w-full h-auto bg-[#F8F8F5]"
              />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-0.5 bg-[#F97316]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">What Happens Next</span>
            </div>
            <h2 className="font-heading text-2xl tracking-tight text-[#1A1A1A] leading-tight" style={{ fontWeight: 800 }}>
              I&rsquo;ll get back to you personally, usually within one business day.
            </h2>
            <ul className="mt-6 space-y-3.5 text-sm">
              {[
                "I read every message myself.",
                "If your project is a good fit, we’ll talk it through.",
                "If I’m not the right person to help, I’ll tell you.",
                "Your information stays with me.",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[#2B2F33]/85 leading-relaxed">
                  <Check className="w-4 h-4 text-[#F97316] mt-1 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-8">
            {submitted ? (
              <div className="border border-[#1A1A1A]/10 bg-[#F8F8F5] p-10 lg:p-14" data-testid="contact-success">
                <div className="w-12 h-12 rounded-full border-2 border-[#F97316] flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#F97316]" />
                </div>
                <h3 className="font-heading text-3xl tracking-tight text-[#1A1A1A] mt-8" style={{ fontWeight: 900 }}>Thanks. Message received.</h3>
                <p className="mt-4 text-base text-[#2B2F33]/85 leading-relaxed max-w-xl">
                  Todd will review your project details and reply personally, usually within one business day. For urgent matters, call <a href={SITE.phoneHref} className="text-[#F97316] font-semibold">{SITE.phone}</a>.
                </p>
                <button className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#1A1A1A] link-underline" onClick={() => setSubmitted(false)} data-testid="contact-new-submission">
                  Submit Another Project <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-[#1A1A1A]/10 p-8 lg:p-12 space-y-6" data-testid="contact-form" encType="multipart/form-data">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Name" required>
                    <input value={form.name} onChange={setField("name")} required data-testid="contact-name"
                      className="w-full border-b border-[#E2E8F0] py-3 bg-transparent focus:outline-none focus:border-[#1A1A1A] transition-colors text-[#1A1A1A]" />
                  </Field>
                  <Field label="Company">
                    <input value={form.company} onChange={setField("company")} data-testid="contact-company"
                      className="w-full border-b border-[#E2E8F0] py-3 bg-transparent focus:outline-none focus:border-[#1A1A1A] transition-colors text-[#1A1A1A]" />
                  </Field>
                  <Field label="Phone" required>
                    <input type="tel" value={form.phone} onChange={setField("phone")} required data-testid="contact-phone-input"
                      className="w-full border-b border-[#E2E8F0] py-3 bg-transparent focus:outline-none focus:border-[#1A1A1A] transition-colors text-[#1A1A1A]" />
                  </Field>
                  <Field label="Email" required>
                    <input type="email" value={form.email} onChange={setField("email")} required data-testid="contact-email-input"
                      className="w-full border-b border-[#E2E8F0] py-3 bg-transparent focus:outline-none focus:border-[#1A1A1A] transition-colors text-[#1A1A1A]" />
                  </Field>
                </div>

                <Field label="Project Address">
                  <input value={form.project_address} onChange={setField("project_address")} data-testid="contact-address"
                    placeholder="Street, City, ZIP"
                    className="w-full border-b border-[#E2E8F0] py-3 bg-transparent focus:outline-none focus:border-[#1A1A1A] transition-colors text-[#1A1A1A] placeholder:text-[#2B2F33]/40" />
                </Field>

                <Field label="Project Type" required>
                  <div className="flex flex-wrap gap-3 mt-2" data-testid="contact-project-type">
                    {PROJECT_TYPES.map((opt) => (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => setForm((f) => ({ ...f, project_type: opt }))}
                        data-testid={`project-type-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                        className={`text-sm font-semibold px-5 py-2.5 border-2 transition-colors ${
                          form.project_type === opt ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#E2E8F0] text-[#2B2F33] hover:border-[#1A1A1A]"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Project Description">
                  <textarea
                    value={form.project_description}
                    onChange={setField("project_description")}
                    rows={5}
                    data-testid="contact-description"
                    placeholder="Tell Todd what you&rsquo;re working on, the property, timing, and any constraints."
                    className="w-full border border-[#E2E8F0] py-3 px-4 bg-transparent focus:outline-none focus:border-[#1A1A1A] transition-colors text-[#1A1A1A] placeholder:text-[#2B2F33]/40 resize-y"
                  />
                </Field>

                <Field label="Send a Quote, Photo, or Document (optional)">
                  {!file ? (
                    <label className="mt-2 flex items-center gap-3 border-2 border-dashed border-[#E2E8F0] hover:border-[#F97316] hover:bg-[#F8F8F5] cursor-pointer p-5 transition-colors" data-testid="contact-file-drop">
                      <Upload className="w-5 h-5 text-[#F97316]" />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-[#1A1A1A]">Click to attach a file</div>
                        <div className="text-xs text-[#5F6B73] mt-0.5">A quote, a photo of an existing sign, a building shot, a landlord sign criteria document, a permit comment sheet — whatever helps. Max {MAX_FILE_MB}MB.</div>
                      </div>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.heic"
                        onChange={onFileChange}
                        className="hidden"
                        data-testid="contact-file-input"
                      />
                    </label>
                  ) : (
                    <div className="mt-2 flex items-center gap-3 border-2 border-[#F97316] bg-[#F8F8F5] p-4" data-testid="contact-file-attached">
                      <Upload className="w-5 h-5 text-[#F97316]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#1A1A1A] truncate">{file.name}</div>
                        <div className="text-xs text-[#5F6B73]">{(file.size / 1024).toFixed(0)} KB</div>
                      </div>
                      <button type="button" onClick={clearFile} className="text-[#5F6B73] hover:text-[#F97316]" data-testid="contact-file-remove" aria-label="Remove file">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </Field>

                <div className="pt-2 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="text-xs text-[#2B2F33]/60">No spam. No automated drip emails. Todd reads every inquiry himself.</div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center gap-2 transition-colors"
                  >
                    {submitting ? "Sending..." : "Request a Project Call"}
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#2B2F33]/60 font-bold mb-2">
        {label}{required && <span className="text-[#F97316] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
